import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription, Observable, forkJoin } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

// ---------Shared--------------------------------------------
import { System } from '../../shared-components/System';
import { UserService } from '../../shared-components/user/user.service';
import { StudentService } from '../../shared-components/student/student.service';
import { YearService } from '../../shared-components/year/year.service';
import { Student } from '../../shared-components/student/student.model';
import { StudentAttachment } from '../../shared-modules/student-attachment/student-attachment.model';
import { IepService } from './iep.service';
import { SpedCollection, DocumentFieldData, SpedDocument, SpedDocumentAttachment } from './iep.model';
import { IepDocumentsComponent } from './iep-documents/iep-documents.component';

// -------Angular Material------------------------------------
import { TylerMatTableDataSource } from '../../shared-components/angular-material/extended.class';
import { AriaDirective } from '../../shared-components/directives/aria.directive';
import { detailExpand } from '../../shared-components/angular-material/mat-animations';
import { LoginService } from '../../shared-modules/login/login.service';


@AutoUnsubscribe()
@Component({
    selector: 'app-iep',
    templateUrl: './iep.component.html',
    animations: [detailExpand],
    styles: []
})
export class IepComponent implements OnInit, OnDestroy {

    // ------------------------------------------------------------
    selectedStudent: Student;
    selectedStudentSubscription: Subscription;
    spedCollection: SpedCollection[];
    spedDocuments: DocumentFieldData[];
    docAttachments: SpedDocumentAttachment[] = [];
    iepData: SpedCollection[] = [];
    rowExpanded: boolean[];
    iepDocumentsDialogRef: MatDialogRef<IepDocumentsComponent>;
    isBusy: boolean;
    SDMIntegration: boolean;
    V10: boolean;

    // ----------Angular Material------------------------------------
    angularMatData = new TylerMatTableDataSource();
    columnsToDisplay: string[] = ['CollectionName', 'Expand'];
    columnsToDisplayDetailRow: string[] = [];

    constructor(
        private userService: UserService,
        private studentService: StudentService,
        private yearService: YearService,
        private iepService: IepService,
        private dialog: MatDialog,
        private loginService: LoginService
    ) {
        this.selectedStudentSubscription = studentService.selectedStudentStream$.subscribe(
            studentId => {
                this.loadStudentInfo(studentId);
            });
    }

    ngOnInit() {
        this.V10 = this.loginService.LoginSettings.V10;
        this.SDMIntegration = this.loginService.LoginSettings.SDMIntegration;
        this.loadStudentInfo(this.studentService.selectedStudent.Id);
    }

    ngOnDestroy() {
    }

    loadStudentInfo(studentId: number) {
        this.selectedStudent = this.studentService.students.filter(function (student) { return student.Id == studentId; })[0];
        this.getSpedCollection();
    }

    getSpedCollection() {
        this.isBusy = true;
        this.spedCollection = [];
        const serverCalls: Observable<any> = forkJoin(
            this.iepService.getSpedCollection(this.selectedStudent.Id, this.yearService.student360Year.Id, this.userService.userId),
            this.iepService.getSpedDocuments(this.selectedStudent.Id, 0, this.userService.userId),
            this.iepService.getSpedDocumentAttachments(this.selectedStudent.Id, 0, this.userService.userId)
        );

        serverCalls.subscribe(results => {
            let docFields: DocumentFieldData[] = [];
            let docAttachments: SpedDocumentAttachment[] = [];

            if (results[0] != null) {
                this.spedCollection = results[0];
            }
            if (results[1] != null) {
                docFields = results[1];
            }
            if (results[2] != null) {
                docAttachments = results[2];
            }

            const documents: SpedDocument[] = [];
            docFields.forEach(element => {
                if (documents.filter(function (doc) { return doc.SpedCollectionToStudentId == element.SpedCollectionToStudentId; }).length == 0) {
                    const objDoc: SpedDocument = {
                        StudentID: element.StudentID,
                        SpedCollectionId: element.SpedCollectionId,
                        SpedCollectionToStudentId: element.SpedCollectionToStudentId,
                        CollectionStatusId: element.CollectionStatusId,
                        TemplateVersionId: element.TemplateVersionId,
                        Attachment: element.Attachment,
                        ShowAttachmentColumn: element.ShowAttachmentColumn,
                        FieldData: [],
                        FieldValue: {}
                    };

                    objDoc.FieldData = Object.assign(docFields.filter(function (field) { return field.SpedCollectionToStudentId == element.SpedCollectionToStudentId; }));
                    const dictionaryData: { [key: string]: string } = {};
                    if (objDoc.FieldData != undefined) {
                        objDoc.FieldData.forEach(data => {
                            dictionaryData[data.FieldDescription] = data.FieldValue;
                        });
                        objDoc.FieldValue = dictionaryData;
                    }
                    documents.push(objDoc);
                }

                for (let i = 0; i < this.spedCollection.length; i++) {
                    this.spedCollection[i].Documents = [];
                    this.spedCollection[i].Fields = [];
                    if (this.spedCollection[i].CollectionName == 'Imported Documents') {
                        this.spedCollection[i].Documents = docAttachments;
                        this.spedCollection[i].Documents.forEach(elem => {
                            elem.FieldValue = {
                                Description: elem.DocumentDescription,
                                Filename: elem.DocumentName,
                                Created: elem.CreatedDate,
                                User: elem.AttachedBy
                            };
                        });
                        this.spedCollection[i].Fields = ['Description', 'Filename', 'Created', 'User'];
                    }
                    else {
                        for (let j = 0; j < documents.length; j++) {
                            if (this.spedCollection[i].Id == documents[j].SpedCollectionId) {
                                this.spedCollection[i].Documents.push(documents[j]);
                            }
                        }

                        if (this.spedCollection[i].Documents.length > 0) {
                            this.spedCollection[i].Documents[0].FieldData.sort((a, b) => {
                                if (a.ShowOnSummaryGrid < b.ShowOnSummaryGrid) {
                                    return -1;
                                }
                                else if (b.ShowOnSummaryGrid < a.ShowOnSummaryGrid) {
                                    return 1;
                                }
                                else {
                                    return 0;
                                }
                            });

                            for (let k = 0; k < this.spedCollection[i].Documents[0].FieldData.length; k++) {
                                if (this.spedCollection[i].Documents[0].FieldData[k].FieldDescription && this.spedCollection[i].Fields.indexOf(this.spedCollection[i].Documents[0].FieldData[k].FieldDescription) == -1) {
                                    this.spedCollection[i].Fields.push(this.spedCollection[i].Documents[0].FieldData[k].FieldDescription);
                                }
                            }
                        }
                    }
                }
            });

            this.iepData = [];
            this.spedCollection.forEach(element => {
                if (element.Documents != undefined && element.Documents.length > 0) {
                    this.iepData.push(element);
                }
            });
            this.angularMatData = new TylerMatTableDataSource(this.iepData);
            this.expandAll(true);
            this.isBusy = false;
        });
    }

    getColumnsToDisplay(collectionRow: SpedCollection) {
        this.columnsToDisplayDetailRow = ['View'];
        if (collectionRow != null || collectionRow != undefined) {
            if (collectionRow.Fields != undefined) {
                if (collectionRow.Fields.length > 0) {
                    collectionRow.Fields.forEach(field => {
                        if (field != undefined && field != '' && this.columnsToDisplayDetailRow.indexOf(field) == -1) {
                            this.columnsToDisplayDetailRow.push(field);
                        }
                    });
                }
                if (collectionRow.CollectionName != 'Imported Documents') {
                    this.columnsToDisplayDetailRow.push('Attachment');
                }
            }
        }
        return this.columnsToDisplayDetailRow;
    }

    columnsToDisplayCount(objFields: string[]) {
        this.columnsToDisplayDetailRow = ['View'];
        if (objFields.length > 0) {
            objFields.forEach(field => {
                if (field != undefined && field != '' && this.columnsToDisplayDetailRow.indexOf(field) == -1) {
                    this.columnsToDisplayDetailRow.push(field);
                }
            });
        }
        this.columnsToDisplayDetailRow.push('Attachment');
        return this.columnsToDisplayDetailRow.length;
    }

    viewDocumentAttachmentDialog(document) {
        this.iepDocumentsDialogRef = this.dialog.open(IepDocumentsComponent, {
            data: document,
            disableClose: true,
            minWidth: '750px'
        });
    }

    viewDocumentClick(document, collectionName) {
        if (document.SpedDocumentId != null && document.SpedDocumentId != undefined) {
            const studentAttachment: StudentAttachment = {
                DocumentId: document.SpedDocumentId,
                DocumentName: 'IEPAttachedDocument.pdf',
                DocumentBase64: '',
                DocumentMimeType: '',
                DocumentModuleName: 'Documents',
                ReportParameters: [],
                ReportType: 0
            };

            window.localStorage.setItem('studentAttachment', JSON.stringify(studentAttachment));
            const win = window.open(System.baseUrl + 'studentattachment', '_blank');
            win.focus();
        }
        else {
            let win = undefined;
            if (!window.navigator)
                win = window.open(System.baseUrl + 'studentattachment', '_blank');
            this.iepService.viewCollectionDocument(document.SpedCollectionId,
                this.selectedStudent.Id,
                document.SpedCollectionToStudentId,
                document.CollectionStatusId,
                document.TemplateVersionId,
                this.userService.userId,
                this.userService.currentUser.LoginSchoolId,
                this.yearService.student360Year.Id).subscribe(docBase64String => {

                    const byteCharacters = atob(docBase64String);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const FileAsBlob = new Blob([byteArray], { type: 'application/pdf' });
                    window.open(URL.createObjectURL(FileAsBlob), '_blank');
                });
        }
    }

    toggleExpanded(row: any) {
        this.angularMatData.toggleExpanded(row);
    }

    checkExpanded(row: any): boolean {
        return this.angularMatData.checkExpanded(row);
    }

    expandAll(expand: boolean) {
        this.angularMatData.expandAll(expand);
    }

    // ------------Aria/Accessibility---------------------------
    getAriaRow(row: SpedCollection): string {
        const colList: string[] = ['CollectionName'];
        const dataList: string[] = [row.CollectionName];
        return AriaDirective.concatAriaStrings(colList, dataList) + (row.Documents.length > 0 ? ' Expand for more details' : ' No additional details');
    }

    getAriaDetailRow(fields: any, row: any): string {
        const colList: string[] = this.getColumnsToDisplay(fields);
        const dataList: string[] = [];
        for (let index = 0; index < colList.length; index++) {
            if (row.FieldValue[colList[index]] != undefined) {
                dataList.push(row.FieldValue[colList[index]]);
            }
            else {
                dataList.push('');
            }
        }

        return AriaDirective.concatAriaStrings(colList, dataList);
    }

    to_trustedHtml(html_code) {
        if (html_code != null) {
            html_code = JSON.parse(JSON.stringify(html_code)).replace(new RegExp('\n', 'g'), '<br/>');
            html_code = html_code.replace(new RegExp('<br/>', 'g'), ' ');
        }
        return html_code;
    }
}
